import React from "react";
import { FiCheck, FiX, FiZap } from "react-icons/fi";
import "./ThreeStepIndicator.css";
import { t } from "../i18n";

type OrderStatus = "pending" | "accepted" | "queued" | "played" | "declined";

interface Props {
  status: OrderStatus;
  labels?: [string, string, string];
  isPriority?: boolean;
  /** ISO timestamp set by the server when the DJ has opened their
   *  Requests page and seen this pending order. Drives the 'Seen ✓'
   *  read-receipt that replaces the pulsing dots. */
  seenByDjAt?: string | null;
  /** The DJ countered with a higher amount. On the wire that's still
   *  status "declined" (+ declineReason "more_money" + counterAmount) —
   *  but it's an OPEN offer awaiting the fan's answer, so it renders as a
   *  gold "Countered" end state with a respond nudge, not the red X. */
  isCountered?: boolean;
  /** "radar" = dark filled nodes + pinging ring on the active step.
   *  Same DOM/logic, pure CSS re-skin. */
  variant?: "classic" | "radar";
}

export default function ThreeStepIndicator({
  status,
  labels = [t("threeStepIndicator.request"), t("threeStepIndicator.waitingForReply"), t("threeStepIndicator.accepted")],
  isPriority = false,
  seenByDjAt = null,
  isCountered = false,
  variant = "radar",
}: Props) {
  const isAcceptedLike = ["accepted", "queued", "played"].includes(status);
  const isCounterOffer = status === "declined" && isCountered;
  const isDeclined = status === "declined" && !isCounterOffer;
  const isSeenWaiting = status === "pending" && !!seenByDjAt;

  const displayLabels: [string, string, string] = [
    labels[0],
    // Once the artist has answered we're no longer "Waiting for Reply". A
    // counter IS the reply, so it takes the middle spot ("Countered") and the
    // end stays the goal state ("Accepted") the fan can still reach.
    isCounterOffer
      ? t("threeStepIndicator.countered")
      : isAcceptedLike || status === "declined"
      ? t("threeStepIndicator.replied")
      : labels[1],
    isDeclined ? t("threeStepIndicator.declined") : labels[2],
  ];

  const doneSet = new Set<number>(isAcceptedLike ? [0, 1, 2] : [0]);

  const isSecondActive = !isAcceptedLike && status !== "declined";

  const theme = isPriority ? "gold" : "green";
  const link12 = theme;
  const link23 = isCounterOffer ? "gold" : isDeclined ? "red" : isAcceptedLike ? theme : "muted";

  const DoneIcon = isPriority ? FiZap : FiCheck;

  return (
    <div
      className={`stepper3 ${isDeclined ? "stepper3--declined" : ""} ${
        isPriority ? "stepper3--priority" : ""
      } ${variant === "radar" ? "stepper3--radar" : ""}`}
      aria-label={t("threeStepIndicator.progress")}
    >
      <div className={`st ${doneSet.has(0) ? "st--done" : ""}`}>
        <span className="st__dot">
          {doneSet.has(0) ? (
            <DoneIcon
              className={`st__icon ${isPriority ? "st__zap" : "st__check"}`}
              aria-hidden
            />
          ) : (
            <span className="st__num">1</span>
          )}
        </span>
        <span className="st__label">{displayLabels[0]}</span>
      </div>

      <div className={`st__link st__link--${link12}`} aria-hidden />

      <div
        className={`st ${doneSet.has(1) ? "st--done" : ""} ${
          isSecondActive ? "st--active" : ""
        } ${isCounterOffer ? "st--counter-mid" : ""}`}
      >
        <span className="st__dot">
          {isCounterOffer ? (
            <FiZap className="st__icon st__zap" aria-hidden />
          ) : doneSet.has(1) ? (
            <DoneIcon
              className={`st__icon ${isPriority ? "st__zap" : "st__check"}`}
              aria-hidden
            />
          ) : status === "pending" ? (
            <span className="st__dots" aria-hidden>
              <i></i><i></i><i></i>
            </span>
          ) : (
            <span className="st__num">2</span>
          )}
        </span>
        <span className="st__label">{displayLabels[1]}</span>
        {isSeenWaiting && (
          <span className="st__seen-badge" aria-live="polite">
            {t("threeStepIndicator.seen")}
          </span>
        )}
      </div>

      <div className={`st__link st__link--${link23}`} aria-hidden />

      <div
        className={`st ${doneSet.has(2) ? "st--done" : ""} ${
          isDeclined ? "st--decline-end" : ""
        }`}
      >
        <span className="st__dot">
          {isDeclined ? (
            <FiX className="st__x" aria-hidden />
          ) : doneSet.has(2) ? (
            <DoneIcon
              className={`st__icon ${isPriority ? "st__zap" : "st__check"}`}
              aria-hidden
            />
          ) : (
            <span className="st__num">3</span>
          )}
        </span>
        <span className="st__label">{displayLabels[2]}</span>
        {isCounterOffer && (
          <span className="st__respond-badge" aria-live="polite">
            {t("threeStepIndicator.tapToRespond")}
          </span>
        )}
      </div>
    </div>
  );
}
